import { ExportDetailsType } from '@ukpostbox/client-data';
import {
	EmailControl,
	FlexGap,
	FormWrapperMinimum,
	StackVertical,
	Surface,
	TelephoneControl,
	ToggleSwitch,
	ToggleType,
	ValidationError,
	ValidationPatterns,
} from '@ukpostbox/frontend-framework';
import { Controller, FormProvider, useForm, useFormContext } from '@ukpostbox/frontend-framework/lib/ReactHookForm';
import React, { useEffect, useState } from 'react';
import { useForwardingBatchProvider } from '../../Providers/ForwardingBatchProvider';
import { RenderBatchSectionType } from '../../Types/ForwardingBatchTypes';
import { AccountNamesCombobox } from '../Controls/AccountNamesCombobox';

type ExportDetailsSectionType = {
	onChange: () => void;
};

function ShipmentDetailsSection(props: ExportDetailsSectionType) {
	const {
		control,
		watch,
		formState: { defaultValues },
	} = useFormContext();

	useEffect(() => {
		const subscription = watch((e) => {
			console.log('watch', e);
		});

		return () => subscription.unsubscribe();
	}, [watch]);

	return (
		<>
			<h6>Batch Content Summary</h6>
			<StackVertical gap={FlexGap.Medium}>
				<Controller
					control={control}
					name='shipmentDetails.reasonForExportType'
					defaultValue={defaultValues?.shipmentDetails.reasonForExportType}
					rules={{ required: true }}
					render={({ field, fieldState }) => (
						<FormWrapperMinimum invalid={fieldState.invalid} validationMessage={fieldState.error?.message}>
							<StackVertical gap={FlexGap.ExtraSmall}>
								<ToggleSwitch
									name='ContentType'
									label='Gift'
									type={ToggleType.Radio}
									onChange={(e) => {
										field.onChange(1);
										props.onChange();
									}}
									defaultChecked={
										defaultValues?.shipmentDetails.reasonForExportType !== null &&
										defaultValues?.shipmentDetails.reasonForExportType === 1
									}
								/>
								<ToggleSwitch
									name='ContentType'
									label='Other'
									type={ToggleType.Radio}
									onChange={(e) => {
										field.onChange(2);
										props.onChange();
									}}
									defaultChecked={
										defaultValues?.shipmentDetails.reasonForExportType !== null &&
										defaultValues?.shipmentDetails.reasonForExportType === 2
									}
								/>
								<ToggleSwitch
									name='ContentType'
									label='Commercial Sample'
									type={ToggleType.Radio}
									onChange={(e) => {
										field.onChange(3);
										props.onChange();
									}}
									defaultChecked={
										defaultValues?.shipmentDetails.reasonForExportType !== null &&
										defaultValues?.shipmentDetails.reasonForExportType === 3
									}
								/>
							</StackVertical>
						</FormWrapperMinimum>
					)}
				/>

				<div className='input-group'>
					<label>Reason for Export</label>
					<Controller
						control={control}
						name='shipmentDetails.reasonForExportDescription'
						defaultValue={defaultValues?.shipmentDetails.reasonForExportDescription}
						render={({ field, fieldState }) => (
							<FormWrapperMinimum
								invalid={fieldState.invalid}
								validationMessage={fieldState.error?.message}
							>
								<textarea
									rows={10}
									defaultValue={defaultValues?.shipmentDetails.reasonForExportDescription}
									onChange={(e) => {
										field.onChange(e);
										props.onChange();
									}}
								/>
							</FormWrapperMinimum>
						)}
					/>
				</div>
			</StackVertical>
		</>
	);
}

function ShippersDetailsSection(props: ExportDetailsSectionType) {
	const { documentsOnly } = useForwardingBatchProvider();

	const {
		control,
		formState: { defaultValues },
	} = useFormContext();

	return (
		<>
			<h6>Shipping Info</h6>
			<StackVertical gap={FlexGap.Small}>
				<div className='input-group'>
					<label>Shipper's Name</label>
					<Controller
						control={control}
						name='shipperDetails.name'
						defaultValue={defaultValues?.shipperDetails.name}
						render={({ field, fieldState }) => (
							<FormWrapperMinimum
								invalid={fieldState.invalid}
								validationMessage={fieldState.error?.message}
							>
								<AccountNamesCombobox
									name='name'
									defaultValue={field.value}
									callback={(item) => {
										field.onChange(item.label);
										props.onChange();
									}}
								/>
							</FormWrapperMinimum>
						)}
					/>
				</div>
				<div className='input-group'>
					<label>Contact Name</label>
					<Controller
						control={control}
						name='shipperDetails.contactName'
						defaultValue={defaultValues?.shipperDetails.contactName}
						render={({ field, fieldState }) => (
							<FormWrapperMinimum
								invalid={fieldState.invalid}
								validationMessage={fieldState.error?.message}
							>
								<AccountNamesCombobox
									name='contactName'
									defaultValue={field.value}
									callback={(item) => {
										field.onChange(item.label);
										props.onChange();
									}}
								/>
							</FormWrapperMinimum>
						)}
					/>
				</div>
				<div className='input-group'>
					<label>Contact Number</label>
					<Controller
						control={control}
						name='shipperDetails.contactPhoneNumber'
						defaultValue={defaultValues?.shipperDetails.contactPhoneNumber}
						rules={{
							required: true,
							pattern: { value: ValidationPatterns.telephone, message: `Format is not valid` },
						}}
						render={({ field, fieldState }) => (
							<FormWrapperMinimum
								invalid={fieldState.invalid}
								validationMessage={fieldState.error?.message}
							>
								<TelephoneControl
									name='contactPhoneNumber'
									placeholder=''
									defaultValue={field.value}
									onChange={(e) => {
										field.onChange(e);
										props.onChange();
									}}
								/>
							</FormWrapperMinimum>
						)}
					/>
				</div>

				{!documentsOnly && (
					<>
						<div className='input-group'>
							<label>VAT Number</label>
							<Controller
								control={control}
								name='shipperDetails.vatNumber'
								defaultValue={defaultValues?.shipperDetails.vatNumber}
								render={({ field, fieldState }) => (
									<FormWrapperMinimum
										invalid={fieldState.invalid}
										validationMessage={fieldState.error?.message}
									>
										<input
											type='text'
											name='vatNumber'
											defaultValue={field.value}
											onChange={(e) => field.onChange(e.currentTarget.value)}
											onBlur={() => props.onChange()}
										/>
									</FormWrapperMinimum>
								)}
							/>
						</div>
						<div className='input-group'>
							<label>EORI Number</label>
							<Controller
								control={control}
								name='shipperDetails.eoriNumber'
								defaultValue={defaultValues?.shipperDetails.eoriNumber}
								rules={{
									validate: (value, formValue) => {
										console.log('eoriNumber', value, formValue.shipperDetails.eoriNumberOptOut);
										return (
											formValue.shipperDetails.eoriNumberOptOut ||
											value ||
											"One of 'EORI Number' or 'EORI Number Opt-out' is required"
										);
									},
								}}
								render={({ field, fieldState }) => (
									<FormWrapperMinimum
										invalid={fieldState.invalid}
										validationMessage={fieldState.error?.message}
									>
										<input
											type='text'
											name='eoriNumber'
											defaultValue={field.value}
											onChange={(e) => field.onChange(e.currentTarget.value)}
											onBlur={() => props.onChange()}
										/>
									</FormWrapperMinimum>
								)}
							/>
						</div>

						<Controller
							control={control}
							name='shipperDetails.eoriNumberOptOut'
							defaultValue={defaultValues?.shipperDetails.eoriNumberOptOut}
							render={({ field, fieldState }) => (
								<div className={fieldState.invalid ? 'invalid' : ''}>
									<ToggleSwitch
										name='eoriNumberOptOut'
										label='I declare that the documents & goods I am forwarding outside the UK are for non-commercial purposes and therefore do not require the submission of an EORI number'
										type={ToggleType.Checkbox}
										onChange={(e) => {
											field.onChange(e.currentTarget.checked);
											props.onChange();
										}}
										defaultChecked={field.value}
									/>
									<ValidationError value={fieldState.error?.message} />
								</div>
							)}
						/>
					</>
				)}
			</StackVertical>
		</>
	);
}

function RecipientDetailsSection(props: ExportDetailsSectionType) {
	const { documentsOnly } = useForwardingBatchProvider();
	const {
		control,
		formState: { defaultValues },
	} = useFormContext();

	return (
		<>
			<h6>Recipient Info</h6>
			<StackVertical gap={FlexGap.Small}>
				<div className='input-group'>
					<label>Contact Name</label>
					<Controller
						control={control}
						name='recipientDetails.contactName'
						defaultValue={defaultValues?.recipientDetails.contactName}
						rules={{ required: true }}
						render={({ field, fieldState }) => (
							<FormWrapperMinimum
								invalid={fieldState.invalid}
								validationMessage={fieldState.error?.message}
							>
								<input
									type='text'
									name='contactName'
									defaultValue={defaultValues?.recipientDetails.contactName}
									onChange={(e) => field.onChange(e.currentTarget.value)}
									onBlur={() => props.onChange()}
								/>
							</FormWrapperMinimum>
						)}
					/>
				</div>
				<div className='input-group'>
					<label>Contact Number</label>
					<Controller
						control={control}
						name='recipientDetails.contactPhoneNumber'
						defaultValue={defaultValues?.recipientDetails.contactPhoneNumber}
						rules={{
							required: true,
							pattern: { value: ValidationPatterns.telephone, message: `Format is not valid` },
						}}
						render={({ field, fieldState }) => (
							<FormWrapperMinimum
								invalid={fieldState.invalid}
								validationMessage={fieldState.error?.message}
							>
								<TelephoneControl
									name='contactPhoneNumber'
									placeholder=''
									defaultValue={defaultValues?.recipientDetails.contactPhoneNumber}
									onChange={(e) => {
										field.onChange(e);
										props.onChange();
									}}
								/>
							</FormWrapperMinimum>
						)}
					/>
				</div>
				<div className='input-group'>
					<label>Contact Email</label>
					<Controller
						control={control}
						name='recipientDetails.contactEmailAddress'
						defaultValue={defaultValues?.recipientDetails.contactEmailAddress}
						rules={{
							required: true,
							pattern: { value: ValidationPatterns.email, message: `Format is not valid` },
						}}
						render={({ field, fieldState }) => (
							<FormWrapperMinimum
								invalid={fieldState.invalid}
								validationMessage={fieldState.error?.message}
							>
								<EmailControl
									name='contactEmailAddress'
									placeholder=''
									defaultValue={defaultValues?.recipientDetails.contactEmailAddress}
									onChangeCallback={(e) => {
										field.onChange(e);
										props.onChange();
									}}
								/>
							</FormWrapperMinimum>
						)}
					/>
				</div>

				{!documentsOnly && (
					<div className='input-group'>
						<label>Receiver's VAT/PID No</label>
						<Controller
							control={control}
							name='recipientDetails.vatNumber'
							defaultValue={defaultValues?.recipientDetails.vatNumber}
							render={({ field, fieldState }) => (
								<FormWrapperMinimum
									invalid={fieldState.invalid}
									validationMessage={fieldState.error?.message}
								>
									<input
										type='text'
										name='contactName'
										defaultValue={defaultValues?.recipientDetails.vatNumber}
										onChange={(e) => field.onChange(e.currentTarget.value)}
										onBlur={() => props.onChange()}
									/>
								</FormWrapperMinimum>
							)}
						/>
					</div>
				)}
			</StackVertical>
		</>
	);
}

function BatchExportDetails(props: RenderBatchSectionType) {
	const { UpdateExportDetails, currentBatchData, showCn22, documentsOnly } = useForwardingBatchProvider();

	const [saving, setSaving] = useState<boolean>(false);

	const formMethods = useForm({
		defaultValues: {
			shipperDetails: currentBatchData?.exportDetails.shipperDetails,
			recipientDetails: currentBatchData?.exportDetails.recipientDetails,
			shipmentDetails: currentBatchData?.exportDetails.shipmentDetails,
		},
		mode: 'all',
	});

	const HandleSave = (data: any) => {
		setSaving(true);

		UpdateExportDetails(data as ExportDetailsType)
			.then((r) => console.log(r))
			.finally(() => setSaving(false));
	};

	const HandleBlur = () => {
		console.log('HandleBlur');
	};

	return (
		<FormProvider {...formMethods}>
			<form id='exportDetailsForm' onBlur={formMethods.handleSubmit(HandleSave)}>
				<fieldset disabled={saving || props.readOnly || props.complete}>
					<h5 className='mb-xs'>
						{props.complete ? props.section.name : `Step ${props.sectionIndex}: ${props.section.name}`}
					</h5>

					<Surface extraClass='p-md'>
						<div className='row'>
							{!documentsOnly && showCn22 && (
								<div className='col-md-4'>
									<ShipmentDetailsSection onChange={() => HandleBlur()} />
								</div>
							)}
							<div className='col-md-4'>
								<ShippersDetailsSection onChange={() => HandleBlur()} />
							</div>
							<div className='col-md-4'>
								<RecipientDetailsSection onChange={() => HandleBlur()} />
							</div>
						</div>
					</Surface>
				</fieldset>
			</form>
		</FormProvider>
	);
}

export { BatchExportDetails };
